import type {
  FetchScheduleNutritionSaveToLibrarySuggestionArgs,
  SaveScheduleNutritionToLibraryArgs,
  CreateScheduleNutritionArgs,
  DeleteScheduleNutritionArgs,
  FetchScheduleNutritionArgs,
  FetchOrgScheduleNutritionArgs,
  EditScheduleNutritionArgs,
} from './types/scheduleNutrition.types';
import type { ApiTranslation } from '../schemas/translations.schema';

import { destroy, patch, post, get } from './axios';

export type ApiScheduleNutritionMealGrocery = {
  quantity: number;
  unit: string;
  grocery?: null | {
    translations: Array<ApiTranslation>;
    id: string;
  };
};

export type ApiScheduleNutritionMeal = {
  translations: Array<ApiTranslation>;
  /** Persisted meal author, included by the calendar's Meal association. */
  userId?: null | string;
  organizationId?: null | string;
  /** Signed S3 URL for the meal's cover photo, if it has one. */
  coverSignedUrl?: null | string;
  mealGroceries?: Array<ApiScheduleNutritionMealGrocery>;
  id: string;
};

export type ApiScheduleNutrition = {
  /** Frozen civil day (day-anchored scheduling). */
  businessDate?: null | string;
  meal: ApiScheduleNutritionMeal | null;
  nutritionPlan?: null | { id: string; userId?: null | string };
  nutritionPlanId: null | string;
  organizationId: null | string;
  comment: null | string;
  mealId: null | string;
  firstInCycle: boolean;
  isDeleted: boolean;
  eventType: string;
  trainerId: string;
  createdAt: string;
  updatedAt: string;
  dateTime: string;
  clientId: string;
  status: string;
  id: string;
  /**
   * Save-to-Library — stamped when the trainer saves this meal event into
   * their library, cleared by a content-bearing edit. Never set for a
   * plan-level row (no `mealId`) — the BE refuses those.
   */
  savedToLibraryAt?: null | string;
};

export const createScheduleNutrition = async ({
  locale,
  organizationId,
  ...dto
}: CreateScheduleNutritionArgs) => {
  const response = await post<ApiScheduleNutrition>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/schedule-nutrition`,
    dto,
  );
  return response?.data ?? null;
};

export const deleteScheduleNutrition = ({
  id,
  organizationId,
  removeNutritionPlan,
}: DeleteScheduleNutritionArgs) => {
  const base = organizationId
    ? `/v2/organizations/${organizationId}/schedule-nutrition`
    : '/v2/schedule-nutrition';
  const path = removeNutritionPlan
    ? `${base}/remove-nutrition-plan/${id}`
    : `${base}/${id}`;
  return destroy(path);
};

export const fetchScheduleNutrition = async ({
  clientId,
  from,
  organizationId,
  to,
}: FetchScheduleNutritionArgs) => {
  const base = organizationId
    ? `/v2/organizations/${organizationId}/schedule-nutrition`
    : '/v2/schedule-nutrition';
  const response = await get<{ data: ApiScheduleNutrition[]; total: number }>(
    `${base}?clientId=${clientId}&from=${from}&to=${to}`,
  );
  return response ?? null;
};

export const fetchOrgScheduleNutrition = async ({
  from,
  organizationId,
  to,
}: FetchOrgScheduleNutritionArgs) => {
  const response = await get<{ data: ApiScheduleNutrition[]; total: number }>(
    `/v2/organizations/${organizationId}/schedule-nutrition?from=${from}&to=${to}`,
  );
  return response ?? null;
};

export const fetchScheduleNutritionSaveToLibrarySuggestion = async ({
  eventId,
  organizationId,
}: FetchScheduleNutritionSaveToLibrarySuggestionArgs) => {
  const base = organizationId
    ? `/v2/organizations/${organizationId}/schedule-nutrition`
    : '/v2/schedule-nutrition';
  const response = await get<{ data: { title: string } }>(
    `${base}/${eventId}/save-to-library-suggestion`,
  );
  return response?.data ?? null;
};

export const saveScheduleNutritionToLibrary = async ({
  eventId,
  organizationId,
  title,
}: SaveScheduleNutritionToLibraryArgs) => {
  const base = organizationId
    ? `/v2/organizations/${organizationId}/schedule-nutrition`
    : '/v2/schedule-nutrition';
  const response = await post<{ id: string; title: string }>(
    `${base}/${eventId}/save-to-library`,
    { title },
  );
  return response?.data ?? null;
};

export const editScheduleNutrition = async ({
  id,
  organizationId,
  ...dto
}: EditScheduleNutritionArgs) => {
  const base = organizationId
    ? `/v2/organizations/${organizationId}/schedule-nutrition`
    : '/v2/schedule-nutrition';
  const response = await patch<ApiScheduleNutrition>(`${base}/${id}`, dto);
  return response?.data ?? null;
};
